import { z } from "zod/v4";
import { DDNSchema, E164Schema } from "./Schemas.js";
import { adrKeys, patientFlags, rdvTypeValues, } from "../cadendar-consts.js";
import { FamilyRanksConsts } from "./Family.js";
export const ImportedAttachmentSchema = z.object({
    filename: z.string(),
    content_type: z.string(),
    data: z.array(z.number()), // Vec<u8> serialized as array of numbers
});
/** Schema for the metadata part of a multipart email import (no binary attachment data) */
export const ImportedEmailMetadataSchema = z.object({
    mailbox: z.enum(["inbox", "sent"]).default("inbox"),
    uid: z.number(),
    message_id: z.string().optional().nullable(),
    sender: z.string(),
    subject: z.string(),
    date: z.string(),
    body_text: z.string().optional().nullable(),
    body_html: z.string().optional().nullable(),
    patient_id: z.string().optional().nullable(),
    categories: z.array(z.string()).optional(),
    /** Attachment metadata only (filenames + content types), binary data comes as multipart file parts */
    attachments: z
        .array(z.object({
        filename: z.string(),
        content_type: z.string(),
    }))
        .optional(),
});
/** @deprecated Use ImportedEmailMetadataSchema + multipart upload instead */
export const ImportedEmailSchema = z.object({
    uid: z.number(),
    message_id: z.string().optional().nullable(),
    sender: z.string(),
    subject: z.string(),
    date: z.string(),
    body_text: z.string().optional().nullable(),
    body_html: z.string().optional().nullable(),
    patient_id: z.string().optional().nullable(),
    attachments: z.array(ImportedAttachmentSchema).optional(),
});
export const FamilleIdSchema = z
    .string()
    .min(7)
    .transform((val) => val);
export const DoctorsIdSchema = z
    .string()
    .min(7)
    .transform((val) => val);
export const PatientsIdSchema = z
    .string()
    .min(7)
    .max(36)
    .transform((val) => val);
export const CadendarSessionsIdSchema = z
    .string()
    .min(7)
    .max(36)
    .transform((val) => val);
export const AppointmentsIdSchema = z
    .string()
    .min(7)
    .transform((val) => val);
const AptRequestIdsSchema = z
    .string()
    .length(36)
    .transform((val) => val);
const PatientIdSchema = z
    .string()
    .min(7)
    .transform((val) => val);
export const newPatientSchema = z
    .object({
    name: z.string().min(2),
    forename: z.string(),
    portablee164: E164Schema.nullable(),
    email: z.string().nullable(),
    email_consent: z.boolean().nullable(),
    sms_consent: z.boolean().nullable(),
    birthdate: DDNSchema,
    patient_flags: z.enum(patientFlags).nullable(),
    famille_id: FamilleIdSchema.nullable(),
    comments: z.string(),
    adr_flag: z.enum(adrKeys).nullable(),
    adr_id: z.string().min(2).nullable(),
    adr_other_text: z.string().nullable(),
    adresseur_id: z.string().min(2).optional(),
    autre_adresseur: z.string().min(2).optional(),
    seen: z.boolean().nullable(),
    former_patient: z.boolean(),
    raf: z.boolean().nullable(),
    logos_file_number: z.number().nullable(),
    genre: z.enum(["M", "F"]).nullable(),
    address: z.string().nullable(),
    address_complement: z.string().nullable(),
    city: z.string().nullable(),
    postal_code: z.string().nullable(),
    insee_number: z.string().length(15).nullable(),
    familly_rank: z.enum([...FamilyRanksConsts]).nullable(),
    maiden_name: z.string().nullable(),
})
    .strict();
export const existingPatientSchema = newPatientSchema
    .extend({
    id: PatientsIdSchema,
    owner: DoctorsIdSchema,
})
    .strip();
export const updatePatientPropsSchema = z.object(existingPatientSchema
    .pick({
    name: true,
    id: true,
    forename: true,
    portablee164: true,
    birthdate: true,
    email: true,
    email_consent: true,
    sms_consent: true,
    famille_id: true,
    comments: true,
    logos_file_number: true,
    address: true,
    address_complement: true,
    city: true,
    postal_code: true,
    insee_number: true,
    genre: true,
    maiden_name: true,
})
    .partial().shape);
const newAptRequestSchema = z
    .object({
    type: z.number(),
    patient_id: PatientIdSchema,
    object: z.string(),
    public_object: z.string(),
    canceled: z.boolean(),
    reminder_checked: z.boolean(),
    duration: z.number(),
    confirm_checked: z.boolean(),
})
    .strict();
const existingAptRequestSchema = newAptRequestSchema.extend({
    id: AptRequestIdsSchema,
    owner: DoctorsIdSchema,
    apt_id: AppointmentsIdSchema.nullable(),
});
export const aptRequestCommitSchema = z.union([
    existingAptRequestSchema,
    newAptRequestSchema,
]);
const PatientMinusAgeConditionSchema = z
    .object({
    type: z.literal("minusAge"),
    value: z.number().int().positive(),
})
    .strict();
const PatientPlusAgeConditionSchema = z
    .object({
    type: z.literal("plusAge"),
    value: z.number().int().positive(),
})
    .strict();
const PatientFlagsConditionSchema = z
    .object({
    type: z.literal("flags"),
    value: z.enum(patientFlags),
})
    .strict();
const PatientReferredConditionSchema = z
    .object({
    type: z.literal("referred"),
})
    .strict();
const PatientNotReferredConditionSchema = z
    .object({
    type: z.literal("notReferred"),
})
    .strict();
const ExistingPatientConditionSchema = z
    .object({
    type: z.literal("existingPatient"),
})
    .strict();
const NewPatientConditionSchema = z
    .object({
    type: z.literal("newPatient"),
})
    .strict();
const RdvTypeConditionSchema = z
    .object({
    type: z.literal("rdvType"),
    value: z.literal(rdvTypeValues).transform((val) => val),
})
    .strict();
const RdvAfterHourConditionSchema = z
    .object({
    type: z.literal("afterHour"),
    value: z.number().int().positive(),
})
    .strict();
const RdvBeforeHourConditionSchema = z
    .object({
    type: z.literal("beforeHour"),
    value: z.number().int().positive(),
})
    .strict();
const ConditionSchema = z.union([
    PatientMinusAgeConditionSchema,
    PatientPlusAgeConditionSchema,
    PatientFlagsConditionSchema,
    PatientReferredConditionSchema,
    PatientNotReferredConditionSchema,
    ExistingPatientConditionSchema,
    NewPatientConditionSchema,
    RdvTypeConditionSchema,
    RdvAfterHourConditionSchema,
    RdvBeforeHourConditionSchema,
]);
const NotMoreRuleSchema = z
    .object({
    type: z.literal("notMore"),
    value: z.number().int().positive(),
    conditionsAND: z.array(ConditionSchema),
})
    .strict();
const ShortCutRuleSchema = z
    .object({
    type: z.literal("shortcut"),
    conditionsAND: z.array(ConditionSchema),
})
    .strict();
export const RuleSchema = z.union([NotMoreRuleSchema, ShortCutRuleSchema]);
export const CalendarDayPropositionLimitSchema = z
    .object({
    days: z.number().int().min(0),
    maxPropositions: z.number().int().min(0),
    type: z.literal("calendarDayLimit"),
})
    .strict();
export const OpendayPropositionLimitSchema = z
    .object({
    maxPropositions: z.number().int().min(0),
    type: z.literal("opendayLimit"),
    openDays: z.number().int().min(0),
})
    .strict();
export const CalendarWeekPropositionLimitSchema = z
    .object({
    maxPropositions: z.number().int().min(1),
    type: z.literal("calendarWeekLimit"),
    weeks: z.number().int().min(1),
})
    .strict();
export const ShortcutPropositionLimitSchema = z
    .object({
    type: z.literal("shortcutLimit"),
    conditionsAND: z.array(ConditionSchema).nonempty(),
})
    .strict();
export const PropositionLimitSchema = z.union([
    CalendarDayPropositionLimitSchema,
    OpendayPropositionLimitSchema,
    CalendarWeekPropositionLimitSchema,
    ShortcutPropositionLimitSchema,
]);
const EnabledScoreSchema = z
    .object({
    enabled: z.boolean(),
    score: z.number().int(),
})
    .strict();
export const RecommendationSettingsSchema = z
    .object({
    version: z.literal(1),
    scoring: z
        .object({
        exactFit: EnabledScoreSchema,
        beginningOfFreePeriod: EnabledScoreSchema,
        smallResidualGap: EnabledScoreSchema,
        regularResidualGaps: EnabledScoreSchema.extend({
            multipleOfMinutes: z.number().int().positive(),
        }),
        preferredEndTimes: z.array(EnabledScoreSchema.extend({
            endMinute: z
                .number()
                .int()
                .min(0)
                .max(24 * 60),
        })),
        adjacentAppointment: EnabledScoreSchema,
    })
        .strict(),
    durationProfiles: z
        .object({
        cadendar: z
            .object({
            defaultDuration: z.number().int().positive(),
            multiplePatients: z.object({ enabled: z.boolean() }).strict(),
            minor: z
                .object({
                enabled: z.boolean(),
                maxAge: z.number().int().min(0),
                duration: z.literal("minimum"),
            })
                .strict(),
            existingPatientShortMotifs: z
                .object({
                enabled: z.boolean(),
                motifs: z.array(z.number().int()),
                duration: z.literal("minimum"),
            })
                .strict(),
        })
            .strict(),
        cadenweb: z
            .object({
            newPatientAdult: z
                .object({
                enabled: z.boolean(),
                duration: z.number().int().positive(),
            })
                .strict(),
            newPatientMinor: z
                .object({
                enabled: z.boolean(),
                maxAge: z.number().int().min(0),
                duration: z.number().int().positive(),
            })
                .strict(),
            shortMotifs: z
                .object({
                enabled: z.boolean(),
                motifs: z.array(z.number().int()),
                duration: z.number().int().positive(),
            })
                .strict(),
            brokenTooth: z
                .object({
                enabled: z.boolean(),
                motif: z.number().int(),
                duration: z.number().int().positive(),
            })
                .strict(),
        })
            .strict(),
    })
        .strict(),
    postponement: z
        .object({
        enabled: z.boolean(),
        thresholdHours: z.number().positive(),
        delayWeeks: z.number().int().nonnegative(),
    })
        .strict(),
    tieBreaking: z
        .object({
        enabled: z.boolean(),
        refreshHours: z.number().int().positive(),
    })
        .strict(),
    presentation: z
        .object({
        cadendar: z
            .object({
            filterByMinimumScore: z.boolean(),
            minimumScore: z.number().int(),
        })
            .strict(),
        cadenweb: z
            .object({
            candidatesPerDay: z.number().int().nonnegative(),
            candidatesPerDayWhenEvening: z.number().int().nonnegative(),
            automaticSearchWeeks: z.number().int().nonnegative(),
            selectOnlyCandidate: z.boolean(),
        })
            .strict(),
    })
        .strict(),
})
    .strict();
// Schémas pour les règles de bloc
const BlocRuleBaseSchema = z
    .object({
    rdvType: z.literal(rdvTypeValues).transform((val) => val),
    duration: z.number().int().positive(), // en minutes
    frequency: z.literal([
        // Fréquences par semaine (limitées par les jours ouverts)
        "1_week",
        "2_week",
        "3_week",
        "4_week",
        "5_week",
        "6_week",
        "7_week",
        // Fréquence par jour (maximum une fois)
        "1_day",
    ]),
    timeLimit: z.literal([
        // Jours avant
        "1_day_before",
        "2_days_before",
        "3_days_before",
        "4_days_before",
        "5_days_before",
        "6_days_before",
        // Semaines avant
        "1_week_before",
        "2_weeks_before",
        "3_weeks_before",
        // Mois avant
        "1_month_before",
        "2_months_before",
        "3_months_before",
        "4_months_before",
        "5_months_before",
        "6_months_before",
    ]),
    conditionsAND: z.array(ConditionSchema), // Support des conditions comme les autres composants
})
    .strict();
const BlocRuleAnytimeSchema = BlocRuleBaseSchema.extend({
    timeMode: z.literal("anytime"),
}).strict();
const BlocRuleBetweenSchema = BlocRuleBaseSchema.extend({
    timeMode: z.literal("between"),
    startTime: z.number().int().min(0).max(1440), // minutes depuis minuit
    endTime: z.number().int().min(0).max(1440), // minutes depuis minuit
}).strict();
const BlocRuleBeforeSchema = BlocRuleBaseSchema.extend({
    timeMode: z.literal("before"),
    endTime: z.number().int().min(0).max(1440), // minutes depuis minuit
}).strict();
const BlocRuleAfterSchema = BlocRuleBaseSchema.extend({
    timeMode: z.literal("after"),
    startTime: z.number().int().min(0).max(1440), // minutes depuis minuit
}).strict();
export const BlocRuleSchema = z.union([
    BlocRuleAnytimeSchema,
    BlocRuleBetweenSchema,
    BlocRuleBeforeSchema,
    BlocRuleAfterSchema,
]);
export const DevisSchema = z.object({
    name: z.string(), //nom du devis
    patientId: z.string(), //uuid foreign key
    date: z.date(),
    devisLogosNumber: z.number().nullable(),
    expiration: z.date(),
});
export const DevisLineSchema = z.object({
    lineNumber: z.number().int(), //line number
    devisId: z.string(), //uuid foreign key
    codeLogos: z.string(), //code logos, personnel à chaque utilisateur
    ccamCode: z.string(), //code CCAM
    tooth: z.array(z.string()), //tooth
    name: z.string(), //nom
    fees: z.string(), //decimal
    base: z.string(), //decimal
    amo: z.string(), //decimal
    tx: z.number().int(), //decimal
    materials: z.string().nullable(), //decimal
});
export const PatientLogosDataSchema = z.object({
    name: z.string(),
    forename: z.string(),
    birthdate: z.string(),
    sexe: z.enum(["F", "M"]),
    address: z.string(),
    address_complement: z.string(),
    city: z.string(),
    postal_code: z.string(),
    phone: z.string().nullable(),
    mobile: z.string().nullable(),
    email: z.string().nullable(),
    logos_file_number: z.number(),
    insee_number: z.string().nullable(),
    maiden_name: z.string(),
});
export const LogosActeDataSchema = z.object({
    page_index: z.number().int().nonnegative(),
    patient_logos_number: z.number().int().nonnegative(),
    timestamp: z.string().datetime({ offset: true }),
    acte_code: z.string(),
    teeth: z.string(),
    description: z.string(),
    ngap_ccam_code: z.string().nullable(),
    amount: z.number(),
    comment: z.string(),
    unknown_code: z.string(),
    prat_code: z.string(),
    pat_date_hour_code: z.string(),
});
export const LogosActeCategoryTypeSchema = z.enum([
    "retard",
    "comment",
    "with_code",
    "fse",
    "rsp",
    "virement",
    "documentScan",
    "payment",
    "invoice",
    "mail",
    "quote",
    "prescription",
    "canceled_apt",
    "no_show_apt",
]);
export const CategorizedLogosActeDataSchema = LogosActeDataSchema.extend({
    category_type: LogosActeCategoryTypeSchema,
    parsed_amount: z.number().nullable(),
});
export const CommitLogosActesSchema = z.object({
    newActes: z.array(LogosActeDataSchema),
    updatedActes: z.array(LogosActeDataSchema),
    reconciliationMode: z.boolean().optional(),
});
//# sourceMappingURL=zodSchemas.js.map